import { useContext, useEffect, useState } from 'react';
import { Feature, useGetFeatureToggles } from '@urbanx/agx.agencies.client';
import { CampaignsApiContext, InProgressFormModelFormType } from '@urbanx/agx.campaigns.client';
import {
  useListProposals,
  useInvalidateUseListProposals,
  type ProposalResponse,
} from '@urbanx/agx.proposals.client';
import {
  AgxButton,
  AgxModal,
  AgxPanel,
  AgxSpacer,
  AgxTextInput,
  AgxTypography,
  type AgxDialogControlProps,
} from '@urbanx/agx.ui-components2';
import * as styles from './ProposalsDebugModal.css';

interface ProposalsDebugModalProps extends AgxDialogControlProps {
  onNavigate: (path: string) => void;
}

export const ProposalsDebugModal = ({
  isOpen,
  setIsDialogOpen,
  onNavigate,
}: ProposalsDebugModalProps) => {
  const feature = useGetFeatureToggles();
  const enabled =
    !feature.isPending &&
    !feature.isError &&
    feature.data?.enabledFeatures?.includes(Feature.Proposals);
  return (
    <AgxModal
      className={styles.debug}
      id="proposals-debug"
      title="Proposals debug"
      type="wide"
      variant="withDivider"
      actionButtonContainerStyle="withDivider"
      isOpen={isOpen}
      setIsDialogOpen={setIsDialogOpen}
      content={
        enabled ? (
          <ProposalShortcuts onClose={() => setIsDialogOpen(false)} onNavigate={onNavigate} />
        ) : (
          <AgxTypography id="proposals-debug-disabled" type="body" size="medium">
            Proposals debug is unavailable while the feature is disabled or its access cannot be
            loaded.
          </AgxTypography>
        )
      }
      actionButtons={
        <AgxButton
          id="proposals-debug-close"
          text="Close"
          variant="hollow"
          onClick={() => setIsDialogOpen(false)}
        />
      }
    />
  );
};

const ProposalShortcuts = ({
  onClose,
  onNavigate,
}: {
  onClose: () => void;
  onNavigate: (path: string) => void;
}) => {
  const campaigns = useContext(CampaignsApiContext);
  const proposals = useListProposals();
  const invalidate = useInvalidateUseListProposals();
  const [search, setSearch] = useState('');
  const [opening, setOpening] = useState<string>();
  const [error, setError] = useState<string>();
  useEffect(() => {
    void invalidate();
  }, []);

  const open = (path: string) => {
    onClose();
    onNavigate(path);
  };
  const openCampaign = async (
    proposal: ProposalResponse,
    destination: 'facts' | 'presentation'
  ) => {
    const campaignId = proposal.subject.campaignId;
    if (!campaignId) return;
    setError(undefined);
    setOpening(proposal.proposalId);
    try {
      const campaign = await campaigns.getCampaign(campaignId);
      const formId = campaign.inProgressForms?.find(
        (form) => form.formType === InProgressFormModelFormType.ListingProposal
      )?.formId;
      if (!formId) throw new Error('This campaign has no in-progress Listing Proposal form.');
      if (destination === 'facts')
        open(
          `/form?${new URLSearchParams({ campaignId, formId, proposalId: proposal.proposalId })}`
        );
      else
        open(
          `/proposals/setup/${campaignId}?${new URLSearchParams({ formId, proposalId: proposal.proposalId })}`
        );
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : 'Unable to open the campaign.');
    } finally {
      setOpening(undefined);
    }
  };

  const records = proposals.data?.groups.flatMap((group) => group.proposals);
  const matching = records?.filter((proposal) =>
    `${proposal.title} ${proposal.proposalId} ${proposal.subject.campaignId ?? ''}`
      .toLowerCase()
      .includes(search.trim().toLowerCase())
  );
  return (
    <AgxSpacer direction="vertical" gap="lg" padding="md">
      <AgxTypography id="proposals-debug-description" type="body" size="medium">
        Development shortcuts to persisted proposals. This is not the production proposals
        dashboard. All routes retain their normal access checks.
      </AgxTypography>
      <AgxSpacer direction="horizontal" gap="md" wrap>
        <AgxButton
          id="proposals-debug-refresh"
          text="Refresh proposals"
          variant="hollow"
          onClick={() => invalidate()}
        />
        <AgxButton
          id="proposals-debug-campaigns"
          text="Campaigns / New Proposal"
          variant="hollow"
          onClick={() => open('/')}
        />
      </AgxSpacer>
      <AgxTextInput
        id="proposals-debug-search"
        label="Find by title, proposal ID or campaign ID"
        type="text"
        defaultValue=""
        onChange={setSearch}
        stretch
      />
      {proposals.isPending && (
        <AgxTypography id="proposals-debug-loading" type="body" size="medium" role="status">
          Loading proposals…
        </AgxTypography>
      )}
      {proposals.isError && (
        <AgxTypography id="proposals-debug-error" type="body" size="medium" role="alert">
          Unable to load proposals. Use Refresh proposals to retry.
        </AgxTypography>
      )}
      {error && (
        <AgxTypography id="proposals-debug-route-error" type="body" size="medium" role="alert">
          {error}
        </AgxTypography>
      )}
      {!proposals.isError && records?.length === 0 && (
        <AgxTypography id="proposals-debug-empty" type="body" size="medium">
          No proposals have been created yet.
        </AgxTypography>
      )}
      {!proposals.isError && records && records.length > 0 && matching?.length === 0 && (
        <AgxTypography id="proposals-debug-no-match" type="body" size="medium">
          No proposals match your search.
        </AgxTypography>
      )}
      {!proposals.isError &&
        matching?.map((proposal) => (
          <AgxPanel
            key={proposal.proposalId}
            id={`debug-proposal-${proposal.proposalId}`}
            header={proposal.title}
          >
            <AgxTypography
              id={`debug-proposal-${proposal.proposalId}-status`}
              type="body"
              size="small"
            >
              {proposal.proposalType} · {proposal.status} · Revision{' '}
              {proposal.currentRevisionNumber}
            </AgxTypography>
            <AgxTypography
              id={`debug-proposal-${proposal.proposalId}-id`}
              type="caption"
              size="small"
            >
              Proposal: {proposal.proposalId}
            </AgxTypography>
            {proposal.subject.campaignId && (
              <AgxTypography
                id={`debug-proposal-${proposal.proposalId}-campaign`}
                type="caption"
                size="small"
              >
                Campaign: {proposal.subject.campaignId}
              </AgxTypography>
            )}
            <AgxSpacer direction="horizontal" gap="sm" wrap>
              <AgxButton
                id={`debug-open-${proposal.proposalId}`}
                text="Open proposal"
                variant="primary"
                onClick={() => open(`/proposals/${proposal.proposalId}`)}
              />
              {proposal.subject.campaignId && (
                <>
                  <AgxButton
                    id={`debug-facts-${proposal.proposalId}`}
                    text="Edit facts"
                    variant="hollow"
                    disabled={opening !== undefined}
                    onClick={() => openCampaign(proposal, 'facts')}
                  />
                  <AgxButton
                    id={`debug-presentation-${proposal.proposalId}`}
                    text="Photos & copy"
                    variant="hollow"
                    disabled={opening !== undefined}
                    onClick={() => openCampaign(proposal, 'presentation')}
                  />
                </>
              )}
            </AgxSpacer>
          </AgxPanel>
        ))}
    </AgxSpacer>
  );
};
